import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Instagram, Calendar, Mail, BookOpen, MoonStar, Waves, Star, Globe, Youtube } from "lucide-react";
import { SITE, bookingHref } from "../lib/site";

// Page Link-in-bio dédiée Instagram — design aligné à la charte (sable / or / noir)
// Volontairement minimaliste, sans header/footer du site pour l'effet "bio link".

const LINKS = [
  {
    label: "Site web",
    sub: "parfaitsimparfaits.fr",
    href: "/",
    icon: Globe,
    testid: "ig-link-site",
    accent: "from-sand-100 to-sand-200",
  },
  {
    label: "Réserver une séance",
    sub: "Formulaire · réponse sous 24 h",
    href: bookingHref(),
    icon: Calendar,
    testid: "ig-link-reserver",
    accent: "from-[#E8E2DA] to-[#F0EBE0]",
  },
  {
    label: "M'écrire un e-mail",
    sub: SITE.email,
    href: `mailto:${SITE.email}`,
    icon: Mail,
    testid: "ig-link-email",
    accent: "from-sand-100 to-sand-200",
  },
  {
    label: "Hypnose ericksonienne",
    sub: "Env. 1h · 80 €",
    href: "/hypnose#services-detail",
    icon: Waves,
    testid: "ig-link-ericksonienne",
    accent: "from-sand-100 to-sand-200",
  },
  {
    label: "Hypnose régressive et spirituelle",
    sub: "Env. 2h · 120 €",
    href: "/hypnose#services-detail",
    icon: MoonStar,
    testid: "ig-link-regressive",
    accent: "from-sand-100 to-sand-200",
  },
  {
    label: "Témoignages",
    sub: "L'expérience de ceux que j'ai accompagné·es",
    href: "/temoignages",
    icon: Star,
    testid: "ig-link-temoignages",
    accent: "from-sand-100 to-sand-200",
  },
  {
    label: "Blog",
    sub: "Articles, méditations & réflexions",
    href: "/blog",
    icon: BookOpen,
    testid: "ig-link-blog",
    accent: "from-sand-100 to-sand-200",
  },
  {
    label: "Chaîne YouTube",
    sub: "@ParfaitsImparfaits",
    href: "https://www.youtube.com/@ParfaitsImparfaits",
    icon: Youtube,
    testid: "ig-link-youtube",
    external: true,
    accent: "from-[#F0EBE0] to-[#E8E2DA]",
  },
];

export default function InstagramLinks() {
  useEffect(() => {
    document.title = "Liens — Parfaits Imparfaits";
    // Empêche l'indexation par les moteurs de recherche
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, follow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  return (
    <div
      data-testid="instagram-bio-page"
      className="min-h-screen bg-sand-100 grain text-sand-900 -mt-px"
      style={{ paddingTop: "0" }}
    >
      {/* Hero */}
      <section className="pt-20 pb-12 px-6 text-center" data-testid="ig-hero">
        <div className="max-w-md mx-auto">
          <img
            src="/img/logo-dark.webp"
            alt="Parfaits Imparfaits"
            width="220"
            height="86"
            className="mx-auto h-16 w-auto mb-8"
            loading="eager"
          />
          <div className="relative mx-auto w-28 h-28 rounded-full overflow-hidden ring-4 ring-white shadow-xl mb-6">
            <img
              src="/img/valentine-portrait.webp"
              alt="Valentine DL"
              className="w-full h-full object-cover"
              loading="eager"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif font-light leading-tight" data-testid="ig-title">
            Valentine <span className="italic text-sand-500">DL</span>
          </h1>
          <p className="mt-3 text-sand-700 font-serif italic">
            Hypnose ericksonienne · Régressive & spirituelle
          </p>
          <p className="mt-2 text-sm text-sand-600">
            Pantin (15 min de Paris) & en ligne (visio)
          </p>

          {SITE.instagram && (
            <a
              href={SITE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ig-link-instagram-handle"
              className="mt-5 inline-flex items-center gap-2 text-sm text-sand-700 hover:text-sand-900 transition-colors"
            >
              <Instagram size={16} />
              @parfaits_imparfaits
            </a>
          )}
        </div>
      </section>

      {/* Links */}
      <section className="px-6 pb-16" data-testid="ig-links">
        <div className="max-w-md mx-auto space-y-3">
          {LINKS.map((l, i) => {
            const Icon = l.icon;
            const isInternal = l.href.startsWith("/") && !l.external;
            const Comp = isInternal ? Link : "a";
            const compProps = isInternal
              ? { to: l.href }
              : {
                  href: l.href,
                  ...(l.external || l.href.startsWith("http")
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {}),
                };

            return (
              <Comp
                key={l.testid}
                {...compProps}
                data-testid={l.testid}
                className="group block rounded-2xl border border-sand-300/70 bg-white/80 backdrop-blur-sm px-5 py-4 transition-all duration-300 hover:bg-white hover:border-sand-500/50 hover:shadow-lg hover:-translate-y-0.5"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="flex items-center gap-4">
                  <div className={`shrink-0 w-11 h-11 rounded-full bg-gradient-to-br ${l.accent} border border-sand-300/60 flex items-center justify-center`}>
                    <Icon size={18} className="text-sand-700 group-hover:text-sand-900 transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-base sm:text-lg text-sand-900 leading-tight">
                      {l.label}
                    </p>
                    <p className="text-xs text-sand-600 mt-0.5 truncate">
                      {l.sub}
                    </p>
                  </div>
                  <svg
                    className="shrink-0 w-4 h-4 text-sand-500 group-hover:text-sand-700 group-hover:translate-x-1 transition-all"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </div>
              </Comp>
            );
          })}
        </div>

        {/* Citation */}
        <div className="max-w-md mx-auto mt-12 text-center">
          <p className="font-serif italic text-sand-700 text-lg leading-relaxed">
            « Comprendre pour avancer, transformer pour se libérer. »
          </p>
        </div>

        {/* Footer mini */}
        <div className="max-w-md mx-auto mt-10 pt-6 border-t border-sand-300/60 text-center text-xs text-sand-600 space-y-1">
          <p>© {new Date().getFullYear()} Parfaits Imparfaits</p>
          <p>
            <Link to="/mentions-legales" data-testid="ig-link-legal" className="hover:text-sand-900">
              Mentions légales
            </Link>
            <span className="mx-2">·</span>
            <Link to="/politique-de-confidentialite" data-testid="ig-link-rgpd" className="hover:text-sand-900">
              Politique de confidentialité
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
